/**
 * TMG · The Mobility Group — canonical brand mark component.
 *
 * The single source of truth for the logo across the site.
 * Renders the 8-point star mark + stacked THE / MOBILITY / GROUP
 * wordmark. All 8 rays are computed from a single inner/outer radius
 * so the star is provably symmetric at every size.
 *
 * Props
 *   variant   'amber' | 'white'        colour (default 'amber')
 *   layout    'horizontal' | 'stacked' mark-left/word-right OR mark-above
 *   size      number                   height of the mark in px (default 40)
 *   showWord  boolean                  render the stacked wordmark (default true)
 *   className string                   extra classes on the outer element
 *   testId    string                   data-testid
 */
export const Logo = ({
  variant = 'amber',
  layout = 'horizontal',
  size = 40,
  showWord = true,
  className = '',
  testId = 'tmg-logo',
}) => {
  const fill = variant === 'amber' ? '#F59E0B' : '#FFFFFF';

  // Star SVG — same math as /brand/v2
  const StarSVG = (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className="shrink-0 block"
      aria-hidden="true"
    >
      <g stroke={fill} strokeWidth="6" strokeLinecap="round" fill="none">
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i * Math.PI) / 4;
          const cx = 50;
          const cy = 50;
          const outer = 36;
          const inner = 14;
          return (
            <line
              key={i}
              x1={cx + inner * Math.cos(a)}
              y1={cy + inner * Math.sin(a)}
              x2={cx + outer * Math.cos(a)}
              y2={cy + outer * Math.sin(a)}
            />
          );
        })}
      </g>
      <circle cx="50" cy="50" r="5.5" fill={fill} />
    </svg>
  );

  // Stacked wordmark — three equal lines, generous tracking
  // Sizing scales with the star: word font-size = star size * 0.24
  const wordSize = Math.max(11, Math.round(size * 0.24));
  const lineH = wordSize * 1.35;
  // Optical alignment: G is curved and appears inset vs flat-edge T/M.
  // Nudge GROUP left by ~2.5% of font-size so all three leading letters
  // visually align to the eye (not just to their bounding boxes).
  const gOpticalNudge = `${-wordSize * 0.025}px`;
  const Word = (
    <div
      className="flex flex-col"
      style={{
        fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif",
        fontWeight: 600,
        fontSize: `${wordSize}px`,
        letterSpacing: '0.28em',
        lineHeight: `${lineH}px`,
        color: fill,
      }}
    >
      <span>THE</span>
      <span>MOBILITY</span>
      <span style={{ marginLeft: gOpticalNudge }}>GROUP</span>
    </div>
  );

  if (layout === 'stacked') {
    return (
      <div
        data-testid={testId}
        className={`inline-flex flex-col items-center gap-2 ${className}`}
      >
        {StarSVG}
        {showWord && Word}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center gap-3 ${className}`}
    >
      {StarSVG}
      {showWord && Word}
    </div>
  );
};

export default Logo;
